import { DiffOptions } from '@monorepo/tools/src/lib/utils/object';
import { CampaignModel } from '../../../models/campaign.model';

type ArrayKeys<T> = { [K in keyof T]-?: NonNullable<T[K]> extends readonly unknown[] ? K : never }[keyof T];
type ItemOf<T, K extends keyof T> = NonNullable<T[K]> extends readonly (infer Item)[] ? Item : never;

// Accessors run on JSON copies of the entity: read fields only, never call model methods.
export type ArrayDiffRule<Item> = {
	identity: (item: Item) => string | number | undefined;
	label: (item: Item) => string | undefined;
	fields: Record<string, (item: Item) => unknown>;
};

export type DiffConfig<T> = {
	ignoreKeys: string[];
	sections: Partial<Record<keyof T & string, string>>;
	arrays: { [K in ArrayKeys<T>]?: ArrayDiffRule<ItemOf<T, K>> };
};

export type ComparisonConfig = {
	ignoreKeys: string[];
	sections: Record<string, string | undefined>;
	arrays: Record<string, ArrayDiffRule<any> | undefined>;
};

export const defineDiffConfig = <T>(config: DiffConfig<T>): DiffConfig<T> => config;

export const isIgnoredKey = (key: string, ignoreKeys: string[]): boolean =>
	ignoreKeys.some(ignored => key === ignored || key.startsWith(`${ignored}.`));

export const getArrayIdentities = (config: ComparisonConfig): NonNullable<DiffOptions['arrayIdentity']> => {
	const identities: NonNullable<DiffOptions['arrayIdentity']> = {};
	for (const [key, rule] of Object.entries(config.arrays)) {
		if (rule) {
			identities[key] = rule.identity;
		}
	}
	return identities;
};

export const campaignDiffConfig = defineDiffConfig<CampaignModel>({
	ignoreKeys: [
		'advertiser.id',
		'advertiser.user_cap_enabled',
		'advertiser.status',
		'advertiser.campaign_groups',
		'advertiser.stats',
		'advertiser.total_budget',
		'campaign_group.id',
		'campaign_group.status',
		'campaign_group.advertiser',
		'targeting.isSharedTargetingModified',
	],
	sections: {
		campaign_destinations: 'Destinations',
		campaign_destination: 'Destinations',
		campaign_phases: 'Phases',
		targeting: 'Targeting',
		daily_budget: 'Daily Budget',
		name: 'Basic Info',
		status: 'Basic Info',
		advertiser: 'Basic Info',
		campaign_group: 'Basic Info',
		campaign_type: 'Basic Info',
		daily_domain_max_loss: 'Basic Info',
	},
	arrays: {
		campaign_destinations: {
			identity: link => link.destination_dto?.id ?? link.selectedDestinationId,
			label: link => {
				const name = link.hydratedDestinationName ?? link.destination_dto?.name ?? link.selectedDestinationDto?.name;
				const id = link.destination_dto?.id ?? link.selectedDestinationId;
				if (name && id != null) {
					return `${name} (${id})`;
				}
				return name ?? (id != null ? String(id) : undefined);
			},
			fields: {
				weight: link => link.weight,
				status: link => link.status,
				cid: link => link.selectedWebappsCid ?? link.webapps_cid_dto?.cid,
			},
		},
		campaign_phases: {
			identity: phaseLink => phaseLink.phase?.id ?? phaseLink.id,
			label: phaseLink => {
				const name = phaseLink.phase?.name;
				const id = phaseLink.phase?.id ?? phaseLink.id;
				if (name && id != null) {
					return `${name} (${id})`;
				}
				return name ?? (id != null ? String(id) : undefined);
			},
			fields: {
				active: phaseLink => phaseLink.active,
			},
		},
	},
});
